/**
 * ClassDialog - Add/Edit class dialog
 *
 * Modal form for creating or editing a class.
 */

import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { LocalClass } from "@/types/local-db.types";

// ============================================================================
// Schema
// ============================================================================

const classSchema = z.object({
  name: z.string().min(1, "Class name is required").max(100, "Name too long"),
  grade: z.string().optional(),
});

type ClassFormData = z.infer<typeof classSchema>;

// ============================================================================
// Constants
// ============================================================================

const GRADES = ["K", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"];

// ============================================================================
// Component
// ============================================================================

interface ClassDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingClass?: LocalClass | null;
  onSubmit: (data: ClassFormData) => void;
}

export function ClassDialog({
  open,
  onOpenChange,
  editingClass,
  onSubmit,
}: ClassDialogProps) {
  const form = useForm<ClassFormData>({
    resolver: zodResolver(classSchema),
    defaultValues: {
      name: "",
      grade: "",
    },
  });

  // Reset form when dialog opens with edit data
  useEffect(() => {
    if (open) {
      if (editingClass) {
        form.reset({
          name: editingClass.name,
          grade: editingClass.grade || "",
        });
      } else {
        form.reset({
          name: "",
          grade: "",
        });
      }
    }
  }, [open, editingClass, form]);

  const handleSubmit = (data: ClassFormData) => {
    onSubmit(data);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {editingClass ? "Edit Class" : "Add Class"}
          </DialogTitle>
          <DialogDescription>
            {editingClass
              ? "Update the class details."
              : "Create a new class to organize students."}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Class Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g., Math 10 Block A"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="grade"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Grade Level (optional)</FormLabel>
                  <Select
                    onValueChange={(value) => field.onChange(value === "__none__" ? "" : value)}
                    value={field.value || "__none__"}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select grade" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="__none__">No grade</SelectItem>
                      {GRADES.map((grade) => (
                        <SelectItem key={grade} value={grade}>
                          {grade === "K" ? "Kindergarten" : `Grade ${grade}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit">
                {editingClass ? "Save Changes" : "Add Class"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
